'use client';

import { logger } from '@adornis/base/logging.js';
import { Bell, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  markConversationAsReadMutation,
  streamUserConversationActivitySubscription,
} from '../../../db/api/get-conversations.js';
import { getAllExamsQuery } from '../../../db/api/get-exams.js';
import type { Exam } from '../../../db/exam.js';
import {
  EXAM_NOTIFICATION_EVENT,
  EXAM_UPDATE_EVENT,
  examEvents,
  triggerExamNotification,
  type ExamNotificationDetail,
} from '../../lib/events.js';
import { useCurrentUser } from '../../lib/useCurrentUser.js';
import { cn } from '../../lib/utils.js';

interface ToastItem {
  id: number;
  detail: ExamNotificationDetail;
}

interface Props {
  onOpenExam: (conversationId: string) => void;
  activeConversationId?: string;
}

const TOAST_TTL_MS = 6000;

export function ExamNotificationToaster({ onOpenExam, activeConversationId }: Props) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [examsById, setExamsById] = useState<Record<string, Exam>>({});
  const nextIdRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const activeConversationIdRef = useRef<string | undefined>(activeConversationId);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const loadExams = useCallback(async () => {
    if (!user) {
      setExamsById({});
      return;
    }
    try {
      const result = await getAllExamsQuery()({
        _id: 1,
        title: 1,
        subject: 1,
        topic: 1,
        class: 1,
        group: 1,
        conversationId: 1,
      });
      const map: Record<string, Exam> = {};
      for (const exam of result) {
        map[exam._id] = exam;
      }
      setExamsById(map);
    } catch (err) {
      logger.warn(err, `ExamNotificationToaster: failed to load exam titles`);
    }
  }, [user]);

  useEffect(() => {
    void loadExams();
    const handleUpdate = () => {
      void loadExams();
    };
    examEvents.addEventListener(EXAM_UPDATE_EVENT, handleUpdate);
    return () => {
      examEvents.removeEventListener(EXAM_UPDATE_EVENT, handleUpdate);
    };
  }, [loadExams]);

  // One subscription per logged-in user that emits when `hasUnreadReply` flips true on any of
  // the user's conversations (the server-side change-stream watcher does the user-vs-agent
  // distinction). Drops a notification (and triggers the sidebar badge) for any non-active
  // conversation. See `streamUserConversationActivitySubscription` for the server-side filter.
  useEffect(() => {
    if (!user) return;
    logger.info(
      `ExamNotificationToaster: starting activity subscription ${JSON.stringify({ userId: user._id, activeConversationId: activeConversationIdRef.current })}`,
      {},
    );
    const subscription = streamUserConversationActivitySubscription()().subscribe({
      next: (rawEvent: unknown) => {
        logger.info(
          `ExamNotificationToaster: received raw activity event ${JSON.stringify({ rawEvent: String(rawEvent ?? ''), activeConversationId: activeConversationIdRef.current })}`,
        );
        try {
          const payload = JSON.parse(String(rawEvent ?? '{}')) as {
            conversationId?: string;
            examId?: string;
          };
          const conversationId = payload?.conversationId;
          const examId = payload?.examId;
          if (!conversationId || !examId) return;
          // The user is already looking at this conversation — silently sync the server
          // (which just flipped `hasUnreadReply: true`) so a refresh doesn't surface a ghost
          // badge for a message they're literally staring at.
          if (activeConversationIdRef.current === conversationId) {
            void markConversationAsReadMutation(conversationId)().catch(err =>
              logger.warn(err, `ExamNotificationToaster: failed to mark active conversation as read`),
            );
            return;
          }
          logger.info(
            `ExamNotificationToaster: triggering toast notification ${JSON.stringify({ examId, conversationId })}`,
          );
          triggerExamNotification({
            examId,
            conversationId,
            kind: 'message',
            message: t('ExamNotifications.newMessage'),
          });
        } catch (err) {
          logger.warn(err, `ExamNotificationToaster: malformed activity event`);
        }
      },
      error: err => {
        logger.warn(err, `ExamNotificationToaster: user activity stream failed`);
      },
    });

    return () => {
      try {
        subscription.unsubscribe();
      } catch (err) {
        logger.warn(err, `ExamNotificationToaster: failed to unsubscribe activity stream`);
      }
    };
  }, [user, t]);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  useEffect(() => {
    const handleNotification = (event: Event) => {
      const detail = (event as CustomEvent<ExamNotificationDetail>).detail;
      if (!detail?.conversationId) return;
      const id = ++nextIdRef.current;
      setToasts(prev => [...prev, { id, detail }]);
      const timer = setTimeout(() => dismiss(id), TOAST_TTL_MS);
      timersRef.current.set(id, timer);
    };
    examEvents.addEventListener(EXAM_NOTIFICATION_EVENT, handleNotification);
    return () => {
      examEvents.removeEventListener(EXAM_NOTIFICATION_EVENT, handleNotification);
    };
  }, [dismiss]);

  useEffect(() => {
    return () => {
      for (const timer of timersRef.current.values()) {
        clearTimeout(timer);
      }
      timersRef.current.clear();
    };
  }, []);

  if (toasts.length === 0) return null;

  const titleFor = (examId: string): string => {
    const exam = examsById[examId];
    if (!exam) return t('Sidebar.examList.unnamedExam', { defaultValue: 'Klausur' });
    if (exam.subject && exam.group) return `${exam.subject} - ${exam.group}`;
    return exam.title || t('Sidebar.examList.unnamedExam', { defaultValue: 'Klausur' });
  };

  const openLabel = t('ExamNotification.open', { defaultValue: 'Öffnen' });
  const dismissLabel = t('ExamNotification.dismiss', { defaultValue: 'Schließen' });

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[60] flex max-w-sm flex-col gap-2">
      {toasts.map(({ id, detail }) => (
        <div
          key={id}
          className={cn(
            'pointer-events-auto flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-card p-3 shadow-lg',
            'animate-in slide-in-from-right-4 fade-in duration-200',
          )}
          role="status"
        >
          <div className="mt-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 p-1.5 text-amber-600 dark:text-amber-400">
            <Bell className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-slate-800 dark:text-slate-200">{titleFor(detail.examId)}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{detail.message}</div>
            <button
              type="button"
              onClick={() => {
                onOpenExam(detail.conversationId);
                dismiss(id);
              }}
              className="mt-2 inline-flex items-center rounded-md bg-brand-500 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-[brand-600]"
            >
              {openLabel}
            </button>
          </div>
          <button
            type="button"
            onClick={() => dismiss(id)}
            aria-label={dismissLabel}
            className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
